<script setup lang="ts">
import { computed, type VNode } from 'vue'
import UiNavLink from '../UiNavLink.vue'
import UiIcon, { type UiIconProps } from '../UiIcon.vue'
import UiText from '../UiText.vue'
import { useButtonBase } from './composables/useButtonBase'
import { 
    getVariantClasses, 
    getTransitionClasses, 
    getBackgroundHoverClasses, 
    getArrowClasses,
    getEffectiveHoverStyle 
} from './utils/buttonClasses'
import type { ButtonBaseProps, ButtonSize, ButtonJustify } from './types'

export interface UiButtonProps extends ButtonBaseProps {
    /** Button size */
    size?: ButtonSize
    /** Text label */
    label?: string
    /** Icon name to show before label */
    icon?: UiIconProps['name']
    /** Show arrow icon after label */
    showArrow?: boolean
    /** Content justification */
    justify?: ButtonJustify
    
    // Legacy props (deprecated, for backward compatibility)
    /** @deprecated Use size="lg" instead */
    shape?: 'rectangle' | 'square' | 'large' | 'plain' | 'icon'
    /** @deprecated Use UiSquareButton or UiIconButton instead */
    noPy?: boolean
    /** @deprecated Use UiIconButton or custom styling instead */
    inline?: boolean
    /** @deprecated Use UiCardButton instead */
    hasSubtitle?: boolean
    /** @deprecated */
    overwriteTransition?: boolean
    /** @deprecated */
    ghostSemibold?: boolean
    /** @deprecated Use font-normal class instead */
    normal?: boolean
}

const props = withDefaults(defineProps<UiButtonProps>(), {
    variant: 'primary',
    size: 'md',
    justify: 'center',
    theme: 'light',
    disabled: false,
    noHover: false,
    type: 'button',
    // Legacy defaults
    shape: undefined,
    noPy: false,
    inline: false,
    hasSubtitle: false,
    overwriteTransition: false,
    ghostSemibold: false,
    normal: false,
})

// Warn about deprecated props in development
if (import.meta.env.DEV) {
    if (props.shape) {
        console.warn('[UiButton] The "shape" prop is deprecated. Use "size" prop or UiIconButton/UiSquareButton components instead.')
    }
    if (props.noPy) {
        console.warn('[UiButton] The "noPy" prop is deprecated. Use UiIconButton or UiSquareButton components instead.')
    }
    if (props.inline) {
        console.warn('[UiButton] The "inline" prop is deprecated. Use custom styling or UiIconButton instead.')
    }
    if (props.hasSubtitle) {
        console.warn('[UiButton] The "hasSubtitle" prop is deprecated. Use UiCardButton component instead.')
    }
    if (props.ghostSemibold) {
        console.warn('[UiButton] The "ghostSemibold" prop is deprecated.')
    }
}

const { isLink, isExternal, effectiveNoHover } = useButtonBase(props)

const effectiveHoverStyle = computed(() => getEffectiveHoverStyle(props.hoverStyle, props.variant))

// Compute effective size from legacy shape prop or new size prop
const effectiveSize = computed(() => {
    if (props.shape === 'large') return 'lg'
    return props.size
})

// Handle legacy shape prop for backward compatibility
const isLegacySquare = computed(() => props.shape === 'square')
const isLegacyIcon = computed(() => props.shape === 'icon')
const isLegacyPlain = computed(() => props.shape === 'plain')

const dynamicClasses = computed(() => {
    const noHover = effectiveNoHover.value || props.noHover

    // Shape/size classes (with legacy support)
    const shapeClasses = {
        'px-3': effectiveSize.value === 'sm' && !props.inline && !isLegacySquare.value && !isLegacyIcon.value,
        'px-4': effectiveSize.value !== 'sm' && !props.inline && !isLegacySquare.value && !isLegacyIcon.value,
        'py-1': effectiveSize.value === 'sm' && !props.noPy && !isLegacySquare.value && !isLegacyIcon.value,
        'py-2': effectiveSize.value === 'md' && !props.noPy && !isLegacySquare.value && !isLegacyIcon.value,
        'py-2.5': effectiveSize.value === 'lg' && !props.noPy && !isLegacySquare.value && !isLegacyIcon.value,
        'p-2': isLegacySquare.value && !props.noPy,
        'p-1': isLegacyIcon.value && !props.noPy,
        'min-h-8': effectiveSize.value === 'sm' && !isLegacyIcon.value && !props.hasSubtitle,
        'min-h-11': effectiveSize.value !== 'sm' && !isLegacyIcon.value && !props.hasSubtitle,
        'min-w-11': isLegacySquare.value,
    }

    // Variant/style classes
    const styleClasses = getVariantClasses({
        variant: props.variant,
        theme: props.theme,
        disabled: props.disabled,
    })

    // Transition classes
    const transitionClasses = props.overwriteTransition 
        ? [] 
        : getTransitionClasses({
            variant: props.variant,
            hoverStyle: effectiveHoverStyle.value,
            theme: props.theme,
            noHover,
        })

    // Background hover classes (excluded when inline)
    const backgroundHoverClasses = (props.inline || props.overwriteTransition)
        ? []
        : getBackgroundHoverClasses({
            variant: props.variant,
            hoverStyle: effectiveHoverStyle.value,
            theme: props.theme,
            noHover,
        })

    return [
        {
            ...styleClasses,
            ...shapeClasses,
            'rounded-md': !isLegacySquare.value && !isLegacyIcon.value,
            'rounded-sm': isLegacySquare.value || isLegacyIcon.value,
            'justify-between': props.justify === 'between',
            'justify-center text-center': props.justify === 'center',
            'justify-start': props.justify === 'start',
            'font-semibold': !props.normal && !props.ghostSemibold,
            'cursor-pointer': !props.disabled,
            [props.class || '']: Boolean(props.class),
        },
        transitionClasses,
        backgroundHoverClasses,
    ]
})

const arrowClasses = computed(() => getArrowClasses({
    variant: props.variant,
    hoverStyle: effectiveHoverStyle.value,
    theme: props.theme,
}))

defineSlots<{
    before_label: () => VNode
    label: () => VNode
    after_label: () => VNode
    subtitle: () => VNode
}>()
</script>

<template>
    <component 
        :is="isLink ? UiNavLink : 'button'"
        :to="isLink && !isExternal ? props.href : undefined"
        :href="isLink ? props.href : undefined"
        :external="isExternal"
        :target="isLink ? props.target : undefined"
        :rel="isLink ? props.rel : undefined"
        :allow-referrer="isLink ? props.allowReferrer : undefined"
        :type="!isLink ? props.type : undefined"
        :disabled="!isLink && props.disabled"
        class="group/ui-button inline-flex gap-1.5 text-se-dark se-max-w-full-nested"
        :class="[
            dynamicClasses,
            props.hasSubtitle ? 'items-start py-2 px-4' : 'items-center'
        ]"
    >
        <!-- Before label slot -->
        <slot name="before_label">
            <UiIcon v-if="props.icon" :name="props.icon" class="w-4 h-4" />
        </slot>

        <!-- Content with subtitle (legacy hasSubtitle support) -->
        <div v-if="props.hasSubtitle" class="inline-flex flex-col items-start text-left">
            <slot name="label">
                <div v-if="props.ghostSemibold && props.label" class="relative">
                    <UiText v-if="props.ghostSemibold" as="button" class="font-semibold" ghost no-wrap :text="label" />
                    <UiText as="button" :class="props.ghostSemibold && 'absolute left-0'" no-wrap :text="label" />
                </div>
                <UiText v-else-if="props.label" as="button" no-wrap :text="label" />
            </slot>
            <slot name="subtitle" />
        </div>

        <!-- Standard content -->
        <template v-else>
            <slot name="label">
                <div v-if="props.ghostSemibold && props.label" class="relative">
                    <UiText as="button" :class="props.ghostSemibold && 'absolute'" no-wrap :text="label" />
                    <UiText v-if="props.ghostSemibold" as="button" class="font-semibold" ghost no-wrap :text="label" />
                </div>
                <UiText v-else-if="props.label" as="button" no-wrap :text="label" />
            </slot>
        </template>

        <!-- Arrow icon -->
        <UiIcon 
            v-if="showArrow" 
            name="arrow-right"
            :class="arrowClasses"
        />

        <!-- After label slot -->
        <slot name="after_label" />
    </component>
</template>
